import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { promoApi, EventItem, formatEventDate, eventMonthLabel } from "../services/promoApi";

interface MonthGroup {
  label: string;
  events: EventItem[];
}

function groupByMonth(events: EventItem[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  let current: MonthGroup | null = null;
  for (const ev of events) {
    const label = eventMonthLabel(ev.start_date);
    if (!current || current.label !== label) {
      current = { label, events: [] };
      groups.push(current);
    }
    current.events.push(ev);
  }
  return groups;
}

const EventRow: React.FC<{ event: EventItem }> = ({ event }) => (
  <div className="flex items-baseline gap-4 py-3 border-b border-slate-100 last:border-0">
    <div className="w-28 shrink-0 text-sm font-semibold text-slate-900">
      {formatEventDate(event.start_date, event.end_date)}
    </div>
    <div className="flex-1 min-w-0">
      {event.url ? (
        <a
          href={event.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="text-base font-medium text-blue-600 hover:underline"
        >
          {event.name}
        </a>
      ) : (
        <span className="text-base font-medium text-slate-900">{event.name}</span>
      )}
      {event.location && (
        <span className="block text-sm text-slate-500 mt-0.5">{event.location}</span>
      )}
    </div>
  </div>
);

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    promoApi
      .getUpcomingEvents()
      .then((data) => {
        if (active) setEvents(data);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const groups = groupByMonth(events);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Helmet>
        <title>Upcoming Tech Events — SaveDelete</title>
        <meta
          name="description"
          content="A curated calendar of upcoming technology events, conferences, and product launches — CES, MWC, Google I/O, Apple WWDC, and more."
        />
        <link rel="canonical" href="https://savedelete.com/events/" />
      </Helmet>

      <h1 className="text-3xl font-bold text-slate-900 mb-2">Upcoming Tech Events</h1>
      <p className="text-slate-500 mb-8">
        Conferences, keynotes, and product launches worth watching.
      </p>

      {loading ? (
        <p className="text-slate-400">Loading events…</p>
      ) : events.length === 0 ? (
        <p className="text-slate-400">No upcoming events right now. Check back soon.</p>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.label}>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2 pb-2 border-b border-slate-200">
                {group.label}
              </h2>
              <div className="rounded-lg border border-slate-200 bg-white px-4">
                {group.events.map((ev, i) => (
                  <EventRow key={`${ev.name}-${i}`} event={ev} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsPage;
